import { useState } from "react";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import ProgressBar from "components/DesignSystem/ProgressBar/ProgressBar";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { SupportPerson } from "components/icons/SupportPerson";
import { AngleDown } from "components/icons/AngleDown";
import { EditDetailsIcon } from "components/icons/EditDetailsIcon";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import { ONBOARDING_LINK } from "constants/meetingRequestUrls";
import { AI_AGENT_OUTCOME_ONBOARDING } from "constants/inkleAI";
import {
  ACCOUNT_SETUP_ENTER_DETAILS_CLICKED,
  ACCOUNT_SETUP_COMPLETE_WITH_AI_CLICKED,
  ACCOUNT_SETUP_REVIEW_AND_PAY_CLICKED,
} from "constants/analyticsEvents";
import { useToast } from "hooks/useToast";
import { useAnalytics } from "hooks/useAnalytics";
import { useStartAIAgentMutation } from "store/apis/chat";
import { BackendError } from "types/utils/error";
import { isEntityComplete } from "v2/utils/taxSetupProgress";
import {
  EntityRef,
  EntitySetupChecklist,
  EntityStatusIcon,
} from "v2/components/FilingsDashboard/EntitySetupChecklist";
import { formatSetupTimeEstimate } from "v2/utils/formatSetupTimeEstimate";
import { GoToDashboardButton } from "v2/components/FilingsDashboard/GoToDashboardButton";
import { useRoleBasedView } from "hooks/useRoleBasedView";

type AccountSetupCardProps = {
  progress: number;
  isSetupComplete: boolean;
  amountToPay: number;
  cartItemsCount?: number;
  entities: EntityRef[];

  progressById: Record<string, number>;

  remainingQuestions: number;

  amountByEntityId: Record<string, number>;
  itemsCountByEntityId: Record<string, number>;
  groupId: string;
  channelUrl: string;
  onOpenSetupModal: () => void;
  onReviewAndPay: () => void;
  onReviewEntity: (entityId: string) => void;
};

type StepId = "tax-details" | "review-file";

const STEP_TAX_DETAILS: StepId = "tax-details";
const STEP_REVIEW_FILE: StepId = "review-file";

const STEP_ONE_LABEL = "Step 1";
const STEP_TWO_LABEL = "Step 2";

const REVIEW_STEP_DESCRIPTION =
  "Review the filings we've identified based on your business information and proceed to payment.";
const REVIEW_STEP_EMPTY_CART_DESCRIPTION =
  "No payment needed. Continue to your dashboard.";

const formatAmount = (amount: number): string =>
  `$${amount.toLocaleString("en-US")}`;

type SetupCardHeaderProps = {
  progress: number;
};

const SetupCardHeader = ({ progress }: SetupCardHeaderProps) => (
  <div className="t-flex t-flex-col t-border-b t-border-l-0 t-border-r-0 t-border-t-0 t-border-solid t-border-neutral-0 t-px-5 t-pb-4 t-pt-3.5">
    <span className="t-mb-1.5 t-text-subtext t-text-text-60">
      Set up your account
    </span>

    <div className="t-flex t-items-center t-gap-3">
      <ProgressBar.Root
        value={progress}
        type="grey"
        className="t-flex-1 !t-h-1.5 !t-rounded-3xl !t-bg-white t-shadow-progress-track-inset"
      >
        <ProgressBar.Indicator
          progress={progress}
          className="t-bg-setup-progress !t-bg-surface-transparent !t-rounded-3xl"
        />
      </ProgressBar.Root>

      <span className="t-text-caption t-text-text-30">{progress}%</span>
    </div>
  </div>
);

type SetupStepHeaderProps = {
  stepLabel?: string;
  title: string;
  isOpen: boolean;
  isMuted?: boolean;
  tag?: React.ReactNode;
  action?: React.ReactNode;
  onToggle: () => void;
};

const SetupStepHeader = ({
  stepLabel,
  title,
  isOpen,
  isMuted = false,
  tag,
  action,
  onToggle,
}: SetupStepHeaderProps) => {
  const caretClassName = classNames("t-transition-transform t-duration-300", {
    "-t-rotate-180": isOpen,
  });

  const labelClassName = classNames("t-text-body-sm-lite", {
    "t-text-neutral-40": isMuted,
    "t-text-text-30": !isMuted,
  });

  const titleClassName = classNames("t-text-subtext", {
    "t-text-neutral-40": isMuted,
    "t-text-text-60": !isMuted,
  });

  const labelGroupClassName = classNames("t-flex t-flex-col", {
    "t-gap-2": isOpen,
    "t-gap-1": !isOpen,
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      className="t-flex t-cursor-pointer t-select-none t-items-start t-justify-between"
    >
      <div className={labelGroupClassName}>
        {stepLabel && <span className={labelClassName}>{stepLabel}</span>}

        <div className="t-flex t-items-center t-gap-2">
          <span className={titleClassName}>{title}</span>
          {tag}
        </div>
      </div>

      <div className="t-flex t-items-center">
        {action}

        <span
          className={classNames("t-flex t-p-2 t-text-text-30", caretClassName)}
        >
          <AngleDown />
        </span>
      </div>
    </div>
  );
};

type EntityPayListProps = {
  entities: EntityRef[];
  progressById: Record<string, number>;
  amountByEntityId: Record<string, number>;
  itemsCountByEntityId: Record<string, number>;
  onReviewEntity: (entityId: string) => void;
};

const EntityPayList = ({
  entities,
  progressById,
  amountByEntityId,
  itemsCountByEntityId,
  onReviewEntity,
}: EntityPayListProps) => (
  <div className="t-flex t-flex-col t-gap-[2px]">
    {entities
      .filter((entity) => (itemsCountByEntityId[entity.id] ?? 0) > 0)
      .map((entity, index, payableEntities) => {
        const paymentAmount = amountByEntityId[entity.id] ?? 0;
        const isLast = index === payableEntities.length - 1;
        const isComplete = isEntityComplete(progressById[entity.id] ?? 0);
        const amountLabel = formatAmount(paymentAmount);

        const handleReview = () => onReviewEntity(entity.id);

        return (
          <div key={entity.id} className="t-flex t-items-stretch t-gap-2">
            <div className="t-flex t-w-4 t-flex-col t-items-center">
              <span className="t-flex t-items-center t-py-0.5">
                <EntityStatusIcon isComplete={isComplete} />
              </span>

              {!isLast && <span className="t-w-px t-flex-1 t-bg-neutral-10" />}
            </div>

            <div className="t-flex t-flex-1 t-flex-col t-gap-0.5 t-pb-2 t-text-body-sm t-text-text-60">
              <span className="t-text-subtext-sm">{entity.name}</span>
              <span>Amount to pay: {amountLabel}</span>
            </div>

            <Button
              customType="primary_outlined"
              size="small"
              onClick={handleReview}
            >
              Review and pay
            </Button>
          </div>
        );
      })}
  </div>
);

export const AccountSetupCard = ({
  progress,
  isSetupComplete,
  amountToPay,
  cartItemsCount,
  entities,
  progressById,
  remainingQuestions,
  amountByEntityId,
  itemsCountByEntityId,
  groupId,
  channelUrl,
  onOpenSetupModal,
  onReviewAndPay,
  onReviewEntity,
}: AccountSetupCardProps) => {
  const { isAdmin, isCustomer } = useRoleBasedView();
  const history = useHistory();
  const { alertToast } = useToast();
  const { trackEvent } = useAnalytics();
  const [startAIAgent, { isLoading: isStartingAIAgent }] =
    useStartAIAgentMutation();

  const [openStepOverride, setOpenStepOverride] = useState<StepId | null>();

  const isMultiEntity = entities.length > 1;
  const isCartEmpty = cartItemsCount === 0;
  const shouldShowGoToDashboard = isSetupComplete && isCartEmpty;
  const amountLabel = formatAmount(amountToPay);
  const reviewStepDescription = shouldShowGoToDashboard
    ? REVIEW_STEP_EMPTY_CART_DESCRIPTION
    : REVIEW_STEP_DESCRIPTION;

  const defaultOpenStep = isSetupComplete ? STEP_REVIEW_FILE : STEP_TAX_DETAILS;
  const openStep =
    openStepOverride === undefined ? defaultOpenStep : openStepOverride;

  const isTaxDetailsOpen = openStep === STEP_TAX_DETAILS;
  const isReviewFileOpen = openStep === STEP_REVIEW_FILE;

  const toggleStep = (step: StepId) => {
    setOpenStepOverride(openStep === step ? null : step);
  };

  const handleToggleTaxDetails = () => toggleStep(STEP_TAX_DETAILS);
  const handleToggleReviewFile = () => toggleStep(STEP_REVIEW_FILE);

  const handleTalkToTeam = () =>
    window.open(ONBOARDING_LINK, "_blank", "noopener,noreferrer");

  const handleEnterDetails = () => {
    trackEvent(ACCOUNT_SETUP_ENTER_DETAILS_CLICKED, {
      is_inkle_admin: isAdmin,
    });
    onOpenSetupModal();
  };

  const handleEditDetails = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleEnterDetails();
  };

  const handleReviewAndPay = () => {
    trackEvent(ACCOUNT_SETUP_REVIEW_AND_PAY_CLICKED, {
      is_inkle_admin: isAdmin,
    });
    onReviewAndPay();
  };

  const handleReviewEntity = (entityId: string) => {
    trackEvent(ACCOUNT_SETUP_REVIEW_AND_PAY_CLICKED, {
      is_inkle_admin: isAdmin,
    });
    onReviewEntity(entityId);
  };

  const handleCompleteWithAI = async (): Promise<void> => {
    trackEvent(ACCOUNT_SETUP_COMPLETE_WITH_AI_CLICKED, {
      is_inkle_admin: isAdmin,
    });

    try {
      if (isAdmin) {
        history.push(`/admin/crm/${groupId}/complete-with-ai`);
      }

      if (isCustomer) {
        history.push("/complete-with-ai");
      }

      await startAIAgent({
        channelUrl,
        outcomeName: AI_AGENT_OUTCOME_ONBOARDING,
        groupId,
      }).unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const timeEstimate = formatSetupTimeEstimate(remainingQuestions);

  const completedTag = isSetupComplete ? (
    <Tag tagType="green" rounded size="small" icon>
      Completed
    </Tag>
  ) : undefined;

  const editDetailsAction = isSetupComplete ? (
    <Button
      customType="ghost_icon"
      size="small"
      aria-label="Edit tax details"
      onClick={handleEditDetails}
    >
      <EditDetailsIcon />
    </Button>
  ) : undefined;

  return (
    <div className="t-flex t-flex-col t-gap-5">
      <div className="t-flex t-max-h-[500px] t-flex-col t-overflow-hidden t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey">
        <div className="t-flex t-min-h-0 t-flex-1 t-flex-col t-overflow-y-auto">
          <SetupCardHeader progress={progress} />

          <div className="t-flex t-shrink-0 t-flex-col t-gap-2 t-p-5">
            <SetupStepHeader
              stepLabel={STEP_ONE_LABEL}
              title="Add your tax details"
              tag={completedTag}
              action={editDetailsAction}
              isOpen={isTaxDetailsOpen}
              onToggle={handleToggleTaxDetails}
            />

            {isTaxDetailsOpen && (
              <div className="t-flex t-flex-col t-gap-5">
                <span className="t-text-body-sm t-text-text-30">
                  Provide ownership information and details of all locations
                  your company is conducting business in.
                </span>

                {isMultiEntity && (
                  <EntitySetupChecklist
                    entities={entities}
                    progressById={progressById}
                  />
                )}

                {!isSetupComplete && (
                  <div className="t-flex t-flex-col t-gap-5">
                    <div className="t-flex t-flex-col t-gap-4">
                      <Button
                        customType="primary"
                        size="small"
                        block
                        onClick={handleEnterDetails}
                      >
                        Enter details
                      </Button>
                      <Button
                        customType="secondary"
                        size="small"
                        block
                        onClick={handleCompleteWithAI}
                        disabled={isStartingAIAgent}
                        isLoading={isStartingAIAgent}
                      >
                        ✨ Complete with AI
                      </Button>
                    </div>

                    <span className="t-text-center t-text-caption-italic t-text-neutral-50">
                      Takes {timeEstimate} · Helps identify all mandatory
                      filings applicable
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="t-flex t-shrink-0 t-flex-col t-gap-2 t-border-b-0 t-border-l-0 t-border-r-0 t-border-t t-border-solid t-border-neutral-10 t-p-5">
            <SetupStepHeader
              stepLabel={STEP_TWO_LABEL}
              title="Review, Pay and file taxes"
              isOpen={isReviewFileOpen}
              isMuted={!isSetupComplete}
              onToggle={handleToggleReviewFile}
            />

            {isReviewFileOpen && (
              <div className="t-flex t-flex-col t-gap-5">
                <span className="t-text-body-sm t-text-text-30">
                  {reviewStepDescription}
                </span>

                {shouldShowGoToDashboard && (
                  <GoToDashboardButton groupId={groupId} />
                )}

                {!shouldShowGoToDashboard && isMultiEntity && (
                  <EntityPayList
                    entities={entities}
                    progressById={progressById}
                    amountByEntityId={amountByEntityId}
                    itemsCountByEntityId={itemsCountByEntityId}
                    onReviewEntity={handleReviewEntity}
                  />
                )}

                {!shouldShowGoToDashboard && !isMultiEntity && (
                  <>
                    <span className="t-text-body-sm t-text-text-30">
                      Amount to pay:{" "}
                      <span className="t-text-subtext-sm t-text-text-100">
                        {amountLabel}
                      </span>
                    </span>

                    <div className="t-flex t-flex-col t-gap-2">
                      <Button
                        customType="primary"
                        size="small"
                        block
                        onClick={handleReviewAndPay}
                      >
                        Review and pay {amountLabel}
                      </Button>

                      <span className="t-text-center t-text-caption-italic t-text-neutral-50">
                        Secure checkout
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="t-flex t-items-start t-gap-2 t-py-3">
        <span className="t-flex t-items-center t-p-1.5 t-text-neutral-50">
          <SupportPerson />
        </span>

        <div className="t-flex t-flex-1 t-flex-col t-gap-0.5">
          <span className="t-text-body-sm-lite t-text-text-60">
            Want our team to help with the onboarding or clarify doubts before
            you proceed?
          </span>

          <Button
            customType="link"
            size="small"
            rightIcon={<SmallRightArrow />}
            onClick={handleTalkToTeam}
          >
            Talk to the team
          </Button>
        </div>
      </div>
    </div>
  );
};
